// useAfeBudgetData — the AFE line-item budget store (RM-14 / AFE S1).
//
// An `AfeBudget` is the authored line container 1:1 with an `Afe` register record (scope = pad|well).
// The FE persists ONLY the as-entered `lines` (+ the dropped/penetrated riders) — EVERY total /
// allocation / D&C split is Python-computed and EPHEMERAL, never stored here (Charter Abs-2 +
// red-team S1-B: no persisted "stale cached verdict"; the sole frozen number is a future approval
// snapshot).
//
// ─── SERVER-BACKED AS OF 2026-09-06 ──────────────────────────────────────────
//
// This store used to write `oxbo-afe-budgets-v1` in localStorage, and that is what broke. The PO's
// origin measured 98.7% full — 2,588,456 characters against a 5 MiB UTF-16 quota, of which 4.62 MiB
// was a dead spine key nothing reads — and three of his four authored AFEs saved a header and lost
// their lines. `docs/REVIEW_LOG.md` 2026-09-06 carries the measurements. Budgets now live in
// Postgres, served by `apps/compute-server/blueprints/afe_store.py`.
//
// WHY THE READS ARE STILL SYNCHRONOUS. Consumers call `getBudgetByAfeId` during render and inside
// effects, and `seedAfeDemo` is a plain module with no React at all. Making reads async would
// convert every caller and drag a Redux Provider into suites that deliberately have none. So this
// takes the shape `features/wells/store/spineStore.ts` already proved on the spine: a module cache
// read synchronously, hydrated in the background, published through `useSyncExternalStore`. PB-04
// measured that pattern's blast radius at ONE file precisely because the read surface did not move.
//
// WRITES ARE ASYNCHRONOUS AND THEY REJECT. Every caller that must know whether the money landed
// observes the promise: `useAfeBuilder`'s autosave and `resetToSkeleton`, `AfeStartPage`'s create
// (which awaits before it navigates), and `AfeSection`'s clone (which awaits before it publishes a
// funding link). All four were converted and their failure paths mutation-tested BEFORE this
// change, because a rejected write sailing past a synchronous catch is AFE-15 F4 with a network in
// the middle.
//
// LOCALSTORAGE REMAINS THE FALLBACK, and ONLY for unreachable. CLAUDE.md's API Strategy keeps it as
// a development fallback so a developer with no compute server running still has a working app. The
// fallback fires on "the server is not there" and NEVER on "the server said no": a 409 is the write
// precondition working, and quietly writing it to localStorage instead would convert a refused
// write into a silent divergence between this browser and the database — worse than the failure it
// hides.

import { useCallback, useSyncExternalStore } from "react";
import type { AfeBandPercentiles, AfeBudget } from "../types";
import { AfeStorageError } from "./useAfeData";
import {
  AfeBudgetRemoteError,
  fetchBudget,
  forgetEtag,
  writeBudget,
} from "../store/afeBudgetRemote";

/** localStorage key for the fallback path. Bump the suffix if the shape changes. */
export const AFE_BUDGETS_STORAGE_KEY = "oxbo-afe-budgets-v1";

export interface UseAfeBudgetDataReturn {
  budgets: AfeBudget[];
  getBudgets: () => AfeBudget[];
  getBudgetByAfeId: (afeId: string) => AfeBudget | undefined;
  /** Create or replace the budget for an afeId (1:1 with the AFE).
   *
   *  REJECTS when the write does not land. A caller that ignores the result silently drops a
   *  money failure, which is the defect this store was rebuilt to end. */
  upsertBudget: (budget: AfeBudget) => Promise<void>;
  /** Drop a budget from the local view. Abs-7: there is NO server delete — archival rides the
   *  AFE's own archive sub-resource, and this destroys nothing remote. */
  deleteBudget: (afeId: string) => Promise<void>;
}

function readJSON<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

// THROWS on failure — it must not swallow. A silently-dropped budget write is worse than a loud
// one: the AFE header persists while its LINES vanish, so the user sees a real AFE that has
// quietly lost the cost codes it inherited. Mirrors useAfeData.writeJSON (Charter Abs-10).
function writeJSON<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    throw new AfeStorageError(key, err);
  }
}

// ---------------------------------------------------------------------------
// The module cache. Synchronous to read, published to React by subscription.
// ---------------------------------------------------------------------------

let cache: AfeBudget[] = [];
const listeners = new Set<() => void>();

/** STABLE REFERENCE while nothing has changed. `useSyncExternalStore` compares by identity and
 *  loops forever on a snapshot that allocates a fresh array each call.
 *
 *  LAZY FALLBACK READ, and this is the fallback semantic rather than a convenience. The cache
 *  starts EMPTY and is filled by whichever source answers first: the server, via `hydrateOne`, or
 *  localStorage here. Reading localStorage at module-import time instead would bind the store to
 *  whatever existed the instant the bundle loaded — wrong in the app, where this module can import
 *  before auth resolves, and wrong in tests, where fixtures are written after import. Empty-cache
 *  is the only trigger, so once either source has answered this is a plain, stable return. */
function snapshot(): AfeBudget[] {
  if (cache.length === 0) {
    const local = readJSON<AfeBudget>(AFE_BUDGETS_STORAGE_KEY);
    if (local.length > 0) cache = local;
  }
  return cache;
}

function publish(next: AfeBudget[]): void {
  cache = next;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** afeIds whose hydration has been started, so a read during render does not spawn a fetch per
 *  render. Cleared on failure so a later attempt can retry. */
const hydrating = new Set<string>();

/** Whether this client has finished ASKING the server about an afeId.
 *
 *  SEV-0 THIS EXISTS FOR (adversarial review 2026-09-07, F2). `getBudgetByAfeId` returns
 *  `undefined` on a cold cache and starts a fetch, so a caller that cannot distinguish "no
 *  budget" from "not asked yet" treats every fresh load as an empty AFE. The Builder did
 *  exactly that: it snapshotted the budget in a mount-time `useState` initializer, rendered
 *  the taxonomy skeleton over a real Postgres budget, and the first keystroke PUT that
 *  skeleton back — archiving the authored lines, and NULLing Amounts in place where the
 *  deterministic skeleton ids collided with stored rows. Not a race: render 1 is cold by
 *  construction on every load, for every AFE.
 *
 *  "settled" means the server answered — with a budget, or with a 404 meaning none exists.
 *  A failed fetch does NOT settle: the honest state is still unknown, and a caller must not
 *  read it as empty. */
const settled = new Set<string>();

export type BudgetStatus = "cold" | "loading" | "settled";

/** #65 — record a percentile set the SERVER just confirmed, so every reader of the cache (the
 *  Builder on remount, the program roll-up, clones) sees it. Only ever called with the set
 *  `setAfePercentilesRemote` returned; a refused set never reaches the cache. */
export function setCachedPercentiles(
  afeId: string,
  percentiles: AfeBandPercentiles,
): void {
  const entry = snapshot().find((b) => b.afeId === afeId);
  if (!entry) return;
  publish([
    ...snapshot().filter((b) => b.afeId !== afeId),
    { ...entry, percentiles: { ...percentiles } },
  ]);
}

export function budgetStatus(afeId: string): BudgetStatus {
  if (settled.has(afeId)) return "settled";
  if (hydrating.has(afeId)) return "loading";
  return "cold";
}

function hydrateOne(afeId: string): void {
  if (hydrating.has(afeId)) return;
  hydrating.add(afeId);
  void fetchBudget(afeId)
    .then((budget) => {
      // Settled either way: a 404 is a real answer ("this AFE has no budget"), and treating
      // it as unsettled would leave a genuinely-empty AFE permanently un-editable.
      settled.add(afeId);
      if (!budget) {
        publish([...cache]);
        return;
      }
      publish([...cache.filter((b) => b.afeId !== afeId), budget]);
    })
    .catch((err: unknown) => {
      // Hydration failure is NOT fatal and must not throw into a render. The cache keeps whatever
      // the fallback seeded, and the next WRITE reports honestly if the server is still
      // unreachable. Logged rather than swallowed.
      if (err instanceof AfeBudgetRemoteError && err.unreachable) {
        // UNREACHABLE SETTLES, and this is load-bearing rather than a convenience. In
        // fallback mode localStorage IS the source of truth, so the question "what does the
        // server hold" has been answered as far as it can be. Leaving it unsettled would
        // make the Builder's hydration gate permanent — a developer with no compute server
        // running could never save at all, which is the fallback path broken by the fix
        // meant to protect it.
        settled.add(afeId);
        // eslint-disable-next-line no-console
        console.warn(
          `AFE budget ${afeId}: compute server unreachable; using local fallback.`,
        );
      } else {
        // A server that ANSWERED with an error does NOT settle: what it holds is genuinely
        // unknown, and reading that as "empty" is the defect this gate exists to stop.
        // eslint-disable-next-line no-console
        console.error(`AFE budget ${afeId}: hydration failed`, err);
      }
      hydrating.delete(afeId);
      // PUBLISH even though the cache did not change. `budgetStatus` is read during render,
      // so a settle that does not notify leaves every subscriber holding a stale "loading"
      // and the Builder's gate never opens. The status IS state, and state that nobody is
      // told about is not state.
      publish([...cache]);
    });
}

export function useAfeBudgetData(): UseAfeBudgetDataReturn {
  const budgets = useSyncExternalStore(subscribe, snapshot, snapshot);

  // Through snapshot(), not `cache` directly — a synchronous read must see the same lazy fallback
  // the subscription does, or the two disagree on the first read.
  const getBudgets = useCallback((): AfeBudget[] => snapshot(), []);

  const getBudgetByAfeId = useCallback(
    (afeId: string): AfeBudget | undefined => {
      const found = snapshot().find((b) => b.afeId === afeId);
      // Start hydration for an AFE this client has not loaded. Fire-and-forget: the subscription
      // re-renders the caller when it lands, so the read stays synchronous.
      if (!found) hydrateOne(afeId);
      return found;
    },
    [],
  );

  const upsertBudget = useCallback(async (budget: AfeBudget): Promise<void> => {
    // Optimistic, so the grid stays responsive under an 800 ms debounce. The PREVIOUS entry is
    // kept so a rejection can unwind it — see the catch (F7).
    const previous = snapshot().find((b) => b.afeId === budget.afeId);
    // #65 — the Builder's payload carries lines, not the AFE's percentile set (a header field the
    // budget PUT does not write). Keep the cached set, or every autosave would erase it and the
    // headings, the program roll-up and clones would fall back to 10/50/90 mid-session.
    const pct = budget.percentiles ?? previous?.percentiles;
    budget = pct ? { ...budget, percentiles: pct } : budget;
    publish([...snapshot().filter((b) => b.afeId !== budget.afeId), budget]);

    try {
      await writeBudget(budget);
      // A successful write SETTLES this id: the client now knows what the server holds,
      // because it just put it there. (Missed on the first pass — the replacement did not
      // match after reformatting, so writes were landing without ever settling and the
      // Builder's hydration gate stayed shut on a freshly-written AFE.)
      settled.add(budget.afeId);
    } catch (err) {
      if (err instanceof AfeBudgetRemoteError && err.unreachable) {
        // No server. Fall back so a developer without the compute server still has a working
        // app. This may itself throw AfeStorageError on a full quota — which is correct, and is
        // the loud failure that started all of this. The optimistic entry STAYS: in fallback
        // mode localStorage is the truth and this is what was just written to it.
        writeJSON(AFE_BUDGETS_STORAGE_KEY, cache);
        settled.add(budget.afeId);
        return;
      }

      // F6. A 409 NEEDS A RE-READ, AND NOTHING PERFORMS ONE.
      //
      // `afeBudgetRemote` drops the etag on a 409 with the comment "Drop the etag so the next
      // read re-establishes one". There is no next read. `hydrateOne` fires only on a
      // `getBudgetByAfeId` MISS, and the Builder's three call sites are a mount-time
      // initializer, `discardDraft`, and an adopt effect gated on `!touchedRef.current` — which
      // is false by definition, because the engineer typed or there would have been no save.
      //
      // So the client had no etag, F6 made the precondition mandatory once lines exist, and
      // every retry answered 428. Measured wire log:
      //   GET 200 (E0) → PUT ifMatch=E0 → 409 → PUT ifMatch=null → 428 → PUT ifMatch=null → 428
      // Before F6 the unconditioned retry SUCCEEDED — that was F6's defect — so this terminal
      // state is new on this branch: a silent overwrite was replaced with a permanent lockout,
      // escapable only by reloading the page.
      //
      // A 409 is also the ONE answered failure where the cached budget is known to be STALE:
      // someone else wrote since this client read. Restoring `previous` below would put a
      // known-wrong value back AND satisfy the `!found` gate that guards re-hydration, so the
      // entry is dropped and the re-read triggered explicitly. `fetchBudget` stores the fresh
      // etag on success (afeBudgetRemote.ts), which is what makes the next save conditional
      // again. `settled` is left alone, for F7's reason.
      if (err instanceof AfeBudgetRemoteError && err.status === 409) {
        publish(snapshot().filter((b) => b.afeId !== budget.afeId));
        hydrating.delete(budget.afeId);
        hydrateOne(budget.afeId);
        throw err;
      }

      // F7. UNWIND THE OPTIMISTIC ENTRY. The failure lived in component-scoped state
      // (`saveState`) while the LIE lived in module scope: the never-persisted budget stayed in
      // the cache, `getBudgetByAfeId` then FOUND it so `hydrateOne` never fired, and the client
      // could never learn the server's true state for that AFE. Observed: a reset refused with
      // 503 correctly kept the priced lines and reported "failed" — then one unmount/remount
      // later the cache served the 180-line skeleton as "clean" with no banner, and the next
      // edit committed it for real.
      //
      // Clearing `hydrating` alone would NOT fix it: a phantom entry still satisfies the
      // `!found` test that gates re-hydration. The entry itself has to go.
      if (previous) {
        publish([
          ...snapshot().filter((b) => b.afeId !== budget.afeId),
          previous,
        ]);
      } else {
        publish(snapshot().filter((b) => b.afeId !== budget.afeId));
        // `settled` IS DELIBERATELY LEFT ALONE. It used to be cleared here — "never confirmed
        // by the server, so what is stored is genuinely unknown again" — and that reasoning
        // conflates two different facts. `settled` records that a READ was answered. It was. A
        // failed WRITE says nothing about what that read established.
        //
        // Clearing it deadlocked the AFE (Sev-1, re-review 2026-09-07). `budgetStatus` returned
        // "cold", so `useAfeBuilder`'s `budgetSettled` went false, which
        //   (a) gated OFF the adopt effect — the only thing that could have re-hydrated, since
        //       `hydrateOne` fires solely on a `getBudgetByAfeId` miss and the Builder's other
        //       two call sites are a mount-time initializer and `discardDraft`; and
        //   (b) made `budgetKnown` false, so every later save threw "The saved cost lines have
        //       not loaded yet, so saving now would overwrite them."
        // No load was pending and none could start. Only unmount/remount escaped it.
        //
        // Worse since the 2026-09-07 SEV-0 fix: `linesLoading` is
        // `!budgetSettled && !loaded.hadContent`, so on an AFE seeded from an empty snapshot one
        // failed write also made the grid permanently READ-ONLY, not merely unsaveable.
        //
        // Removing the cache ENTRY above is still correct and still necessary — a phantom entry
        // satisfies the `!found` gate and blocks re-hydration, which is what the comment above
        // it describes. Only the `settled` half was wrong.
      }
      hydrating.delete(budget.afeId);
      // The server ANSWERED and said no. Never paper that over locally.
      throw err;
    }
  }, []);

  const deleteBudget = useCallback(async (afeId: string): Promise<void> => {
    forgetEtag(afeId);
    hydrating.delete(afeId);
    publish(snapshot().filter((b) => b.afeId !== afeId));
    return Promise.resolve();
  }, []);

  return { budgets, getBudgets, getBudgetByAfeId, upsertBudget, deleteBudget };
}

/** TEST SEAM. Resets the module cache between cases — without it the cache leaks across tests,
 *  which is the classic module-store test defect. Not used by application code. */
export function __resetAfeBudgetCacheForTests(seed: AfeBudget[] = []): void {
  hydrating.clear();
  settled.clear();
  // Seeded budgets count as settled: a test that hands the store its fixture has answered
  // the question the server would have, and leaving them "cold" would make every seeded
  // suite wait on a fetch that never comes.
  for (const b of seed) settled.add(b.afeId);
  publish(seed);
}

export default useAfeBudgetData;
